import { InteractionType } from '@debbie/common/dist/interactions/index.js';
import { toggleInSet } from '@debbie/common/dist/util/index.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Button from '../Button.js';
import ButtonDrop from '../ButtonDrop.js';
import { DropDownMenuItem } from '../FilterV2/DropDownMenuItem.js';
import FileIcon from '../Icons/File.js';
import FileText from '../Icons/FileText.js';
import Filter from '../Icons/Filter.js';
import Inbox from '../Icons/Inbox.js';
import Mail from '../Icons/Mail.js';
import Phone from '../Icons/Phone.js';
export const HISTORY_CATEGORIES = [
    'updates',
    'interactions',
    'economy',
    'caseStatus',
    'agentProposals',
    'paymentPlans',
    'tribunal',
    'documents',
];
export const EMPTY_HISTORY_FILTER = {
    categories: new Set(),
    types: new Set(),
    toggledTags: new Set(),
};
export const isCategoryOn = (filter, category) => !filter.categories.has(category);
export const isTypeOn = (filter, type) => !filter.types.has(type);
export const isTagOn = (filter, tag) => filter.toggledTags.has(tag.id) === tag.hiddenByDefault;
const filterCount = (filter) => filter.categories.size + filter.types.size + filter.toggledTags.size;
// Same icons as the interaction tabs on the case page.
const TYPE_ICONS = {
    [InteractionType.Email]: React.createElement(Inbox, { size: "14px" }),
    [InteractionType.Letter]: React.createElement(Mail, { size: "14px" }),
    [InteractionType.Phone]: React.createElement(Phone, { size: "14px" }),
    [InteractionType.SMS]: React.createElement(Phone, { size: "14px" }),
    [InteractionType.Eboks]: React.createElement(FileText, { size: "14px" }),
};
const CaseHistoryFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    const [open, setOpen] = useState(false);
    const { filter } = props;
    const categoryLabels = {
        updates: lang.history.filterUpdates,
        interactions: lang.history.filterInteractions,
        economy: lang.history.filterEconomy,
        caseStatus: lang.history.filterCaseStatus,
        agentProposals: lang.agentProposals.title,
        paymentPlans: lang.paymentPlansInsights.paymentPlans,
        tribunal: lang.tribunal.tribunal,
        documents: lang.documents.documents,
    };
    const visibleTags = props.tags.filter((t) => !t.hiddenByDefault);
    const hiddenTags = props.tags.filter((t) => t.hiddenByDefault);
    // Interactions is the sum of its types: it is on while any type is, and
    // switching it flips every type. Tags only narrow the types that are on, so
    // they keep their state but are locked while none is.
    const allTypes = new Set(props.types.map((t) => t.type));
    const interactionsOn = props.types.length === 0 ||
        props.types.some((t) => isTypeOn(filter, t.type));
    // Hidden tags are off by default, so only the visible ones say whether the
    // user switched part of the category off.
    const childrenOff = filter.types.size + visibleTags.filter((t) => !isTagOn(filter, t)).length;
    const toggleCategory = (category) => {
        if (category === 'interactions') {
            return props.onChange({
                ...filter,
                types: interactionsOn ? allTypes : new Set(),
            });
        }
        props.onChange({
            ...filter,
            categories: toggleInSet(filter.categories, category),
        });
    };
    const toggleType = (type) => props.onChange({ ...filter, types: toggleInSet(filter.types, type) });
    const toggleTag = (tag) => props.onChange({
        ...filter,
        toggledTags: toggleInSet(filter.toggledTags, tag.id),
    });
    const tagRow = (tag) => (React.createElement(DropDownMenuItem, { key: tag.id, indent: true, disabled: !interactionsOn, label: tag.name, count: tag.count, onClick: () => toggleTag(tag), checkbox: {
            checked: isTagOn(filter, tag),
            onChange: () => toggleTag(tag),
        } }));
    return (React.createElement(ButtonDrop, { open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false), closeOnClickOutside: true, minWidth: "260px", customButton: React.createElement(Trigger, null,
            React.createElement(Filter, null),
            filterCount(filter) > 0 && (React.createElement(FilterCount, null, filterCount(filter)))) },
        React.createElement(Panel, null,
            HISTORY_CATEGORIES.filter((category) => category !== 'agentProposals' || props.categoryCounts[category] > 0).map((category) => (React.createElement(React.Fragment, { key: category },
                React.createElement(DropDownMenuItem, { emphasized: true, label: categoryLabels[category], count: props.categoryCounts[category], onClick: () => toggleCategory(category), checkbox: {
                        checked: category === 'interactions'
                            ? interactionsOn
                            : isCategoryOn(filter, category),
                        indeterminate: category === 'interactions' &&
                            interactionsOn &&
                            childrenOff > 0,
                        onChange: () => toggleCategory(category),
                    } }),
                category === 'interactions' && (React.createElement(React.Fragment, null,
                    props.types.length > 0 && (React.createElement(GroupLabel, null, lang.history.filterType)),
                    props.types.map(({ type, count }) => (React.createElement(DropDownMenuItem, { key: type, indent: true, label: lang.interactionTypes[type], count: count, icon: TYPE_ICONS[type] ?? React.createElement(FileIcon, { size: "14px" }), onClick: () => toggleType(type), checkbox: {
                            checked: isTypeOn(filter, type),
                            onChange: () => toggleType(type),
                        } }))),
                    props.tags.length > 0 && (React.createElement(GroupLabel, null, lang.history.filterTags)),
                    visibleTags.map(tagRow),
                    hiddenTags.map(tagRow)))))),
            React.createElement(Actions, null,
                React.createElement(Button, { theme: "link", onClick: () => props.onChange(EMPTY_HISTORY_FILTER) }, lang.history.resetFilter),
                React.createElement(Button, { theme: "link", onClick: () => props.onChange({
                        ...filter,
                        categories: new Set(HISTORY_CATEGORIES.filter((c) => c !== 'interactions')),
                        types: allTypes,
                    }) }, lang.history.hideAll)))));
};
export default CaseHistoryFilter;
const Trigger = styled.div `
  position: relative;
  display: flex;
  align-items: center;

  svg {
    height: 20px;
    width: 20px;
  }
`;
const FilterCount = styled.span `
  position: absolute;
  top: -6px;
  right: -8px;
  padding: 0 6px;
  border-radius: 999px;
  background: ${THEME.COLOR_ACCENT};
  color: ${THEME.COLOR_SECONDARY};
  font-size: 11px;
  line-height: 16px;
`;
const Panel = styled.div `
  padding: 6px 8px;
  text-align: left;
  font-weight: normal;
  font-size: 13px;
  color: ${THEME.COLOR_PRIMARY};
`;
const GroupLabel = styled.div `
  padding: 8px 6px 2px 24px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${THEME.DARK_COLOR_SECONDARY};
`;
const Actions = styled.div `
  display: flex;
  gap: 16px;
  margin-top: 6px;
  padding: 10px 6px 4px;
  border-top: 1px solid ${THEME.BORDER_STRONG};
`;
//# sourceMappingURL=CaseHistoryFilter.js.map